import { useState } from "react";
import { IoFlashOutline } from "react-icons/io5";
import axios from "axios";

declare global {
  interface Window {
    snap: any;
  }
}

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL!;

const PatunganCepat = () => {
  const [nominal, setNominal] = useState("");
  const [isAnonim, setIsAnonim] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [formData, setFormData] = useState({
    nama: "",
    email: "",
    nohp: "",
  });

  const handleNominalClick = (value: number) => {
    setNominal(value.toString());
  };

  const validateForm = () => {
    if (!formData.nohp) return "Nomor HP wajib diisi";
    if (!isAnonim && !formData.nama) return "Nama wajib diisi";
    if (!nominal) return "Nominal wajib diisi";
    if (Number(nominal) < 10000) return "Minimal donasi Rp 10.000";
    return "";
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setLoading(true);

      // ✅ 1. CREATE DONATION
      const createRes = await axios.post(`${API_BASE_URL}/api/donations`, {
        campaign_id: 1,
        amount: Number(nominal),
        name: isAnonim ? "Orang Baik" : formData.nama,
        email: formData.email || null,
        phone: formData.nohp,
        message: null,
      });

      const donationId = createRes.data?.data?.donation_id;

      if (!donationId) {
        throw new Error("Gagal mendapatkan donation ID");
      }

      // ✅ 2. GET SNAP TOKEN
      const detailRes = await axios.get(`${API_BASE_URL}/api/donations/${donationId}`);

      const snapToken = detailRes.data?.donation?.snap_token;

      if (!snapToken) {
        throw new Error("Snap token tidak tersedia");
      }

      // ✅ 3. OPEN MIDTRANS POPUP
      window.snap.pay(snapToken, {
        onSuccess: function () {
          window.location.reload();
        },
        onPending: function () {
          console.log("Menunggu pembayaran...");
        },
        onError: function () {
          setError("Pembayaran gagal");
        },
        onClose: function () {
          console.log("User menutup popup");
        },
      });
    } catch (err) {
      console.error(err);
      setError("Terjadi kesalahan, coba lagi.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="relative w-full overflow-hidden -mt-6 md:-mt-10 mb-16">
      <div
        className="relative max-w-[1440px] mx-auto bg-[#f5f8f6]
        rounded-t-[40px] md:rounded-t-[70px]
        px-4 py-6 md:px-10 md:py-10 overflow-hidden">
        <img src="/images/gedung.png" alt="" className="absolute bottom-0 left-0 w-full opacity-20 pointer-events-none select-none z-0" />

        <div>
          <div className="flex justify-center items-center mb-2 text-2xl md:text-3xl gap-2 text-primary2 ">
            <div className="font-semibold tracking-wide">PATUNGAN CEPAT </div>
          </div>
          <div className="flex justify-center mb-6">
            <div className="bg-secondary w-28 h-2 rounded-full "></div>
          </div>
        </div>

        <div className="relative z-10 flex justify-center">
          <div className="w-full max-w-5xl rounded-3xl shadow-xl p-4 md:p-6">
            <h2 className="text-center text-lg md:text-xl font-bold text-gray-800 mb-8">Isi Data dan Pilih Nominal Donasi</h2>

            <form onSubmit={handleSubmit}>
              <div className="md:flex justify-center gap-4 items-start w-full">
                <div className="w-full flex flex-col gap-3">
                  <div>
                    <label className="text-sm font-semibold text-gray-700">Nama Lengkap</label>
                    <input
                      type="text"
                      value={formData.nama}
                      onChange={(e) => setFormData({ ...formData, nama: e.target.value })}
                      className="w-full mt-2 bg-[#f9fbfa] border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-blue-400 transition"
                      placeholder="Masukkan nama lengkap"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-semibold text-gray-700">Nomor HP</label>
                    <input
                      type="tel"
                      value={formData.nohp}
                      onChange={(e) => setFormData({ ...formData, nohp: e.target.value })}
                      className="w-full mt-2 bg-[#f9fbfa] border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-blue-400 transition"
                      placeholder="08xxxxxxxxxx"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-semibold text-gray-700">Email</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full mt-2 bg-[#f9fbfa] border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-blue-400 transition resize-none"
                      placeholder="Masukkan Email"
                    />
                  </div>
                </div>

                <div className="w-full flex flex-col gap-3 max-sm:mt-3">
                  <div>
                    <label className="text-sm font-semibold text-gray-700">Pilih Nominal</label>

                    <div className="grid grid-cols-4 gap-3 mt-2">
                      {[10000, 25000, 50000].map((value) => (
                        <button
                          type="button"
                          key={value}
                          onClick={() => handleNominalClick(value)}
                          className={`py-3 rounded-xl text-sm font-semibold transition ${nominal === value.toString() ? "bg-primary2 text-white shadow-lg" : "bg-[#eef4f6] text-primary2 hover:bg-blue-200"}`}>
                          Rp {value.toLocaleString("id-ID")}
                        </button>
                      ))}
                    </div>

                    <div className="mt-3">
                      <label className="text-sm font-semibold text-gray-700 ">Nominal Lainnya</label>
                      <input
                        type="number"
                        value={nominal}
                        onChange={(e) => setNominal(e.target.value)}
                        className="w-full mt-2 bg-[#f9fbfa] border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-blue-400 transition"
                        placeholder="Atau masukkan nominal lainnya"
                      />
                    </div>
                  </div>

                  <div className="pt-1">
                    <div className="flex items-center gap-3 mb-4">
                      <input type="checkbox" id="anonim" checked={isAnonim} onChange={(e) => setIsAnonim(e.target.checked)} className="w-4 h-4 accent-primary2 cursor-pointer" />
                      <label htmlFor="anonim" className="text-sm text-gray-700 cursor-pointer">
                        Transaksi sebagai <span className="font-semibold text-primary2">Orang Baik</span>
                      </label>
                    </div>

                    {error && <p className="text-red-500 text-sm mb-3">{error}</p>}

                    <button
                      type="submit"
                      disabled={loading}
                      className="
    w-full py-3 rounded-full
    text-white font-semibold text-sm md:text-base
    bg-gradient-to-r from-primary to-primary2
    shadow-[0_0_10px_rgba(96,165,250,0.6),0_0_30px_rgba(96,165,250,0.5)]
    hover:shadow-[0_0_20px_rgba(96,165,250,0.9),0_0_45px_rgba(96,165,250,0.8)]
    hover:scale-[1.03]
    transition-all duration-300
  ">
                      {loading ? "Memproses..." : "Patungan Sekarang"}
                    </button>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PatunganCepat;
